import React, { useId } from "react";

// The one mark's geometry, unscaled from brand/app-icons/final-heritage-green/citizen/svg/
// foreground.svg (and business/ for the ₹ glyph). scripts/check_single_logo.py fails if any
// path below stops matching the SVG source byte-for-byte — redraw from there, never by hand here.
const PIN = "M54,86 C46,76 30,66 30,50 A24,24 0 1 1 78,50 C78,66 62,76 54,86 Z";
const ROOF = "M45.5,37.5 L49.5,30.5 L53.5,37.5 Z";
const ROAD = "M50.5,64 C46.5,69.5 58,72 52.5,78.5 L56,79.5 C62.5,72 51.5,69.5 57,64 Z";
const CART = "M-6.6,-5.2 H-4.3 L-2.5,2.8 H4.4 L6,-2.6 H-3.6";
const RUPEE = "M-4,-6 H4.5 M-4,-2.5 H4.5 M-4,-6 H-1 C2.8,-6 2.8,1 -1,1 H-4 L3.5,6.5";
const SAFFRON = "#FC8A0C";

/**
 * The Apna Shahar mark: a pin holding a skyline (four buildings, the clock tower, a winding road)
 * under a saffron sun — the same mark as both app icons and the favicon (D-18, R11).
 *
 * **The pin is `currentColor`, and the skyline is cut out of it** (an SVG mask), exactly like the
 * Android 13+ themed icon (monochrome-icon.png). One mark serves several tints — the platform
 * green in the header and footer, teal on the customer app card, ink on the partner app card —
 * and on every background the skyline shows the surface behind it, so there is no fixed cream or
 * green that would vanish on a matching surface. The sun stays a literal saffron: the one fixed
 * accent every tint keeps.
 *
 * `badge` ("cart" | "rupee") adds the app badge from the icons — cart = the customer app, ₹ = the
 * business app. Default none: the web header carries the platform mark, not one app's badge.
 *
 * `aria-hidden` by default. The mark always sits beside the wordmark "Apna Shahar Online", so
 * announcing it again would make a screen reader say the brand twice. Pass a `title` where it
 * appears alone — the language modal does.
 */
export function AsLogo({ size = 40, className = "", title, badge }) {
  // One mask per instance: several logos share a page (header + footer), and a duplicated id
  // would make every instance use the first one's mask.
  const maskId = `as-logo-cut-${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
  return (
    <svg
      width={size}
      height={size}
      viewBox="22 24 64 64"
      className={`block shrink-0 ${className}`}
      role={title ? "img" : undefined}
      aria-label={title || undefined}
      aria-hidden={title ? undefined : true}
      focusable="false"
      data-testid="as-logo"
    >
      {title ? <title>{title}</title> : null}
      <defs>
        <mask id={maskId} maskUnits="userSpaceOnUse" x="0" y="0" width="108" height="108">
          <path d={PIN} fill="#fff" />
          <g fill="#000">
            <rect x="36.5" y="48" width="8.5" height="14" rx="1.5" />
            <rect x="46" y="37" width="7" height="25" />
            <path d={ROOF} />
            <rect x="54" y="45" width="9" height="17" rx="1.5" />
            <rect x="64" y="49.5" width="8" height="12.5" rx="1.5" />
            <rect x="31" y="61" width="46" height="3.6" rx="1.8" />
            <path d={ROAD} />
            {/* The badge's ring is cut too, so the saffron disc sits in a gap, as on the icons. */}
            {badge ? <circle cx="72" cy="71" r="11.8" /> : null}
          </g>
          <circle cx="49.5" cy="42" r="1.7" fill="#fff" />
        </mask>
      </defs>
      <rect x="0" y="0" width="108" height="108" fill="currentColor" mask={`url(#${maskId})`} data-testid="as-logo-pin" />
      <circle cx="66" cy="38" r="6" fill={SAFFRON} data-testid="as-logo-sun" />
      {badge ? (
        <g data-testid={`as-logo-badge-${badge}`}>
          <circle cx="72" cy="71" r="9.2" fill={SAFFRON} />
          <g transform="translate(72 71)" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
            {badge === "rupee" ? (
              <path d={RUPEE} strokeWidth="2.1" />
            ) : (
              <>
                <path d={CART} strokeWidth="1.8" />
                <circle cx="-1.6" cy="5.9" r="1.45" fill="currentColor" stroke="none" />
                <circle cx="3.6" cy="5.9" r="1.45" fill="currentColor" stroke="none" />
              </>
            )}
          </g>
        </g>
      ) : null}
    </svg>
  );
}

/**
 * The full lockup: mark plus "Apna Shahar" over "Online".
 *
 * "Online" is the part that carries the domain, so it is set in the deeper blue at weight 800 —
 * the wordmark reads as one unit while still telling a first-time visitor what to type.
 */
export function AsWordmark({ size = 40, compact = false, className = "" }) {
  return (
    <span className={`flex items-center gap-[11px] ${className}`}>
      <AsLogo size={size} className="text-[var(--as-primary)]" />
      <span className="leading-none">
        <b
          className="block font-display font-extrabold tracking-[-0.02em] text-[var(--as-ink)]"
          style={{ fontSize: compact ? 15 : 18, lineHeight: 1.05 }}
        >
          Apna Shahar
        </b>
        <span className="as-online-word text-[12px] font-extrabold uppercase tracking-[0.1em] text-[var(--as-primary-ink)]">
          Online
        </span>
      </span>
    </span>
  );
}

export default AsLogo;
